import { updateSession } from '../../lib/funnels/functions/v1/default/updateSession'
import { loadGTM } from '../../lib/funnels/functions/v1/default/gtm'
import { loadSentry } from '../../lib/funnels/functions/v1/default/loadSentry'
import { getFunnelCache } from '../../lib/funnels/functions/v1/default/getFunnelCache'
import { updateCustomInput } from '../../lib/funnels/functions/v1/default/updateCustomInput'
import { getInputById } from '../../lib/funnels/functions/v1/default/getInputById'
import { loadCallrail } from '../../lib/funnels/functions/v1/default/loadCallrail'
import { getLoc } from '../../lib/funnels/functions/v1/middleware/getLoc'
import { showInputError } from '../../lib/funnels/functions/v1/html/showInputError'
import { hideInputError } from '../../lib/funnels/functions/v1/html/hideInputError'

import IMask from 'imask'
import { STATES } from '../../lib/funnels/constants/states'

import '@fontsource/roboto'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/700.css'

import './index.css'

const SESSION_ID = 'Tf2Funnel'

/** @type {QuestionIds} */
let TD_QUESTION_IDS = ['paysMoreThan10k', 'state', 'email', 'name', 'phone']
const SECTIONS = ['section-paysMoreThan10k', 'section-submit']

const requiredInputs = ['email', 'name', 'phone']
const requiredSelects = ['']

/**
 * Get location info
 */
async function runGetLoc() {
  try {
    const loc = await getLoc()
    const { region, remoteAddress } = loc
    if (region && remoteAddress) {
      updateSession(SESSION_ID, 'state', region)
      updateSession(SESSION_ID, 'ip', remoteAddress)

      const stateElement = document.getElementById('state-select')
      if (stateElement) stateElement.style.display = 'none'
      TD_QUESTION_IDS = TD_QUESTION_IDS.filter((q) => q !== 'state')
    } else {
      const stateElement = document.getElementById('state')
      if (stateElement) {
        STATES.forEach((state) => {
          const option = document.createElement('option')
          option.value = state.abbreviation
          option.textContent = state.name
          stateElement.appendChild(option)
        })
      }
      requiredSelects.push('state')
    }
  } catch (error) {
    console.error(error)
    const stateElement = document.getElementById('state')
    if (stateElement) {
      STATES.forEach((state) => {
        const option = document.createElement('option')
        option.value = state.abbreviation
        option.textContent = state.name
        stateElement.appendChild(option)
      })
    }
    requiredSelects.push('state')
  }
}

runGetLoc()

window.addEventListener('load', () => {
  loadGTM('GTM-KZ525569')
  loadSentry(
    'https://2c1906e7cc127780564b33c5e6f02954@sentry.turbodevhub.com/14',
  )
  loadCallrail()

  updateSession(SESSION_ID, 'referrer', document.referrer)
})

/**
 * @typedef {(val: string) => void} SetPaysMoreThan10k
 */

/**
 * @type {Window & { setPaysMoreThan10k?: SetPaysMoreThan10k }}
 */
const win = window

document.addEventListener('DOMContentLoaded', async () => {
  win.setPaysMoreThan10k = function (val) {
    updateSession(SESSION_ID, 'paysMoreThan10k', val)
  }

  const blurrable_inputs = document.querySelectorAll('input, select')

  blurrable_inputs.forEach((input) => {
    input.addEventListener('blur', (e) => {
      const target = /** @type {HTMLInputElement} */ (e.target)
      if (!target.value.length) {
        showInputError(target)
      } else if (target.name === 'email') {
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        if (emailPattern.test(target.value)) {
          hideInputError(target)
        } else {
          showInputError(target)
        }
      } else {
        hideInputError(target)
      }
    })
  })

  const phoneEl = document.getElementById('phone')
  if (phoneEl) {
    IMask(phoneEl, {
      mask: '(000) 000-0000',
    })
  }

  // --------------------------------------
  // FUNCTIONS ----------------------------
  // --------------------------------------

  function setProgressBar() {
    const currentQuestion = getCurrentQuestion() + 1
    const totalQuestions = SECTIONS.length + 1
    const progress = Math.round((currentQuestion / totalQuestions) * 100)
    const progressBar = /** @type {HTMLElement | null} */ (
      document.querySelector('.progress-bar-fill')
    )
    if (progressBar) {
      progressBar.style.width = `${progress}%`
    }
  }

  /**
   * Gets an input by id.
   * @param {string} id - The id.
   * @returns {HTMLInputElement} The returned input element.
   */
  function getInput(id) {
    const input = /** @type {HTMLInputElement | null} */ (
      document.getElementById(id)
    )
    if (typeof input === 'undefined' || input === null)
      throw Error(`getInput: ${id} not found`)
    return input
  }

  /**
   * Sets an input value by id.
   * @param {string} id - The id.
   * @param {string} value - The value.
   * @returns {void}
   */
  function setInputValue(id, value) {
    const input = getInput(id)
    input.value = value
  }

  /**
   * Gets a button by id.
   * @param {string} id - The id
   * @returns {HTMLButtonElement} The returned button element
   */
  function getButton(id) {
    const btn = /** @type {HTMLButtonElement | null} */ (
      document.getElementById(id)
    )
    if (btn === null) throw Error(`getButton: ${id} not found`)
    return btn
  }

  /**
   * Disables a button.
   * @param {string} id - The button id.
   * @returns {void}
   */
  function disableButton(id) {
    const btn = getButton(id)
    btn.disabled = true
  }

  /**
   * Enables button.
   * @param {string} id - The button id.
   * @returns {void}
   */
  function enableButton(id) {
    const btn = getButton(id)
    btn.disabled = false
  }

  /**
   * Gets saved responses.
   * @returns {Responses} The user responses object.
   */
  function getSavedReponses() {
    const savedResponses = getFunnelCache(SESSION_ID) || {}
    return {
      email: '',
      name: '',
      paysMoreThan10k: '',
      phone: '',
      state: '',
      ...savedResponses,
    }
  }

  /**
   * Gets saved response from local storage.
   * @param {QuestionId} questionId - The question id.
   * @returns {string} - The value of the response
   */
  function getSavedResponse(questionId) {
    if (!questionId) {
      return ''
    }
    const responses = getSavedReponses()
    return responses[questionId]
  }

  /**
   * Saves response to local storage.
   * @param {QuestionId} questionId - The question id.
   * @param {Answer} answer - The answer value.
   * @returns {void}
   */
  function saveResponse(questionId, answer) {
    // @ts-ignore
    updateSession(SESSION_ID, questionId, answer)
  }

  /**
   * Gets current question from local storage.
   * @returns {number} index - The question index.
   */
  function getCurrentQuestion() {
    const sections = SECTIONS.map((id) => document.getElementById(id))
    const visibleSections = sections.filter(
      (section) => section && !section.classList.contains('hidden'),
    )
    return visibleSections.length - 1
  }

  /**
   * Get query params from url by key
   * @param {string} key - The key
   * @returns {string | null} - The query param value
   */
  function getQueryParams(key) {
    const url = new URL(window.location.href)
    return url.searchParams.get(key)
  }

  /**
   * Checks if required inputs are valid.
   * @param {string[]} inputs - The input ids.
   */
  function checkSubmitInputs(inputs) {
    const invalidInputs = inputs.reduce(
      (validAccumulator, field) => {
        const input = /** @type {HTMLInputElement} */ (
          document.getElementById(field)
        )

        if (input) {
          if (!input.value.length) {
            input.setAttribute('error', '')
            showInputError(input)
            validAccumulator.push(field)
            return validAccumulator.filter((i) => i !== '')
          }
        }
        return validAccumulator.filter((i) => i !== '')
      },
      [''],
    )
    return invalidInputs.filter((i) => i !== '')
  }

  /**
   * Checks if required selects are empty
   * @param {string[]} selects - The input ids
   * @returns {string[]} - The form error state
   */
  function checkSubmitSelects(selects) {
    const invalidSelects = selects.reduce(
      (validAccumulator, field) => {
        const select = /** @type {HTMLSelectElement | null} */ (
          document.getElementById(field)
        )

        if (select) {
          // @ts-ignore
          if (!select.value.length) {
            select.setAttribute('error', '')
            validAccumulator.push(field)
            return validAccumulator.filter((i) => i !== '')
          }
        }

        return validAccumulator.filter((i) => i !== '')
      },
      [''],
    )
    return invalidSelects.filter((i) => i !== '')
  }

  /**
   * Submits form.
   * @returns {Promise<void>}
   */
  async function submit() {
    const invalidInputs = checkSubmitInputs(requiredInputs)
    const invalidSelects = checkSubmitSelects(requiredSelects)

    if (invalidInputs.length > 0 || invalidSelects.length > 0) {
      const firstError = /** @type {HTMLInputElement|HTMLSelectElement} */ (
        document.querySelectorAll('[error]')[0]
      )
      console.error(
        'invalid fields:',
        [...invalidInputs, ...invalidSelects].join(),
      )
      if (firstError) firstError.focus()
      return
    }

    const responses = await getSavedReponses()

    /**
     * Collects user data from the `responses` object.
     * @type {SubmitData}
     */
    const data = {
      actively_paying: 'yes',
      DebtOwed: responses.paysMoreThan10k === 'yes' ? '10000' : '0',
      delinquentDays: '',
      email: responses.email,
      Name: `${responses.name}`,
      paymentPlan: '',
      Phone: responses.phone,
      state: responses.state,
      sub1: getQueryParams('sub1') || '',
      sub2: getQueryParams('sub2') || '',
      sub3: getQueryParams('sub3') || '',
      sub4: getQueryParams('sub4') || '',
      sub5: getQueryParams('sub5') || '',
      trid: getQueryParams('trid') || '',
      oid: getQueryParams('oid') || '',
      utm_campaign: getQueryParams('utm_campaign') || '',
      website: 'funnel-tf-2',
      funnel: 'funnel-tf-2',
    }

    const params = new URLSearchParams(data).toString()
    const url = `https://funnels.turbodebt.com/my-results/?${params}`
    localStorage.removeItem('TD_CACHE')
    window.location.href = url
  }

  /**
   * Enables an ok button.
   * @param {QuestionId} questionId - The question id.
   * @param {boolean} autoClick - if true programatically clicks ok button to progress to next section.
   * @returns {boolean} - True if button enabled, false otherwise.
   */
  function enableOkButton(questionId, autoClick = false) {
    if (!getSavedResponse(questionId)) return false
    if (autoClick) pressOkLogic(questionId)
    if (['email', 'name', 'phone'].includes(questionId)) {
      return true
    }
    enableButton(`${questionId}-button`)
    return true
  }

  /**
   * Selects a form button when clicked (big square buttons).
   * @param {QuestionId} questionId - The question id.
   * @param {Answer} answerValue - The answer value.
   * @returns {void}
   */
  function selectFormButton(questionId, answerValue) {
    const formButtons = document.querySelectorAll('.form-button')
    formButtons.forEach((e) => e.classList.remove('selected'))
    getButton(`${questionId}:${answerValue}`).classList.add('selected')
    enableOkButton(questionId)
  }

  /**
   * Loads a form input.
   * @param {QuestionId} questionId - The question id.
   * @param {Answer} answer - The answer vaue.
   * @returns {void}
   */
  function loadFormInput(questionId, answer) {
    if (!questionId) {
      return
    }
    const theInput = /** @type {HTMLInputElement} */ (
      document.getElementById(questionId)
    )
    setInputValue(questionId, answer)
    if (theInput) {
      updateCustomInput(theInput, answer)
    }
    if (questionId === 'phone') {
      theInput.dispatchEvent(new Event('input'))
    }
    enableOkButton(questionId)
  }

  /**
   * Scroll to section.
   * @param {string} id - The section id.
   * @returns {void}
   */
  function scrollToId(id) {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      })
      const input = el.querySelector('input')
      if (input) {
        setTimeout(() => {
          input.shadowRoot
            ?.querySelector('input')
            ?.focus({ preventScroll: true })
        }, 550) // wait for scroll to complete before focusing
      }
    }
  }

  /**
   * Process input event.
   * @param {InputEvent | Event} event - The input event.
   * @returns {boolean} True if processed successfuly, false otherwise.
   */
  function processInput(event) {
    if (!event.currentTarget) throw new Error('processInput event is null')
    const currentTarget = /** @type {HTMLInputElement} */ (event.currentTarget)
    const questionId = /** @type {QuestionId} */ (currentTarget.id)
    const answer = currentTarget.value

    if (
      questionId &&
      ['email', 'name', 'state', 'phone'].includes(questionId)
    ) {
      saveResponse(questionId, answer)
    }
    // if all other values are set, enable the submit button
    if (TD_QUESTION_IDS.every((q) => getSavedResponse(q))) {
      enableButton('submit-button')
    }

    return true
  }

  /**
   * Returns question and answer form Id.
   * @param {string} id - The id.
   * @param {string} separator - The separator.
   * @returns {QuestionAndAnswer} On object with the question and the answer.
   */
  function questionAndValueFromId(id, separator) {
    const parts = id.split(separator)
    const questionId = /** @type {QuestionId} */ (parts[0])
    const answer = parts[1]
    return { answer, questionId }
  }

  /**
   * Handles click on form button (big square ones)
   * @param {TouchEvent | KeyboardEvent | MouseEvent} e - click event.
   * @returns {boolean} True id click processed correctly, false otherwise
   */
  function clickFormButton(e) {
    const currentTarget = /** @type {HTMLButtonElement} */ (e.currentTarget)
    if (!currentTarget?.id) throw Error('clickFormButton: target id undefined')
    const { answer, questionId } = questionAndValueFromId(currentTarget.id, ':')
    saveResponse(questionId, answer)
    document.querySelectorAll(`.form-button`).forEach((ev) => {
      ev.classList.remove('selected')
    })
    currentTarget.classList.add('selected')
    return enableOkButton(questionId, true)
  }

  /**
   * Checks scroll arrows
   * @returns {void}
   */
  function updateScrollArrows() {
    const els = document.querySelectorAll('section:not(.hidden)')
    const init = els.length === 1
    const currentQuestion = getCurrentQuestion()
    const onLastQuestion = currentQuestion === els.length - 1
    const onFirstQuestion = currentQuestion === 0 && els.length > 1
    if (init) {
      disableButton('down')
      disableButton('up')
      return
    }
    if (onLastQuestion) {
      disableButton('down')
      enableButton('up')
      return
    }
    if (onFirstQuestion) {
      enableButton('up')
      disableButton('up')
      return
    }
    enableButton('down')
    enableButton('up')
  }

  /**
   * Onclick Ok logic
   * @param {QuestionId} questionId - The question Id.
   * @returns {void}
   */
  function pressOkLogic(questionId) {
    const index = TD_QUESTION_IDS.indexOf(questionId)
    const nextQuestionId = SECTIONS[index + 1]
    if (index === SECTIONS.length - 1) {
      submit()
      return
    }
    const nextSection = document.getElementById(nextQuestionId)
    if (nextSection) {
      nextSection.classList.remove('hidden')
      scrollToId(nextSection.id)
      updateScrollArrows()
      setProgressBar()
      if (nextSection.id === 'section-submit') {
        enableButton('submit-button')
      }
    }
  }

  /**
   * detects device
   * @returns {'desktop' | 'mobile'}
   */
  function detectDevice() {
    // @ts-ignore
    const userAgent = navigator.userAgent ?? navigator.vendor ?? window.opera
    if (/android/i.test(userAgent)) {
      return 'mobile'
      // @ts-ignore
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'mobile'
    }
    return 'desktop'
  }

  /**
   * Onclick Ok logic
   * @param {TouchEvent | KeyboardEvent | MouseEvent} event - The click event.
   * @param {string} separator - The separator.
   * @returns {void}
   */
  function pressOk(event, separator = '-') {
    const currentTarget = /** @type {HTMLButtonElement} */ (event.currentTarget)
    if (!currentTarget.id) return
    const { questionId } = questionAndValueFromId(currentTarget.id, separator)
    if (currentTarget.id.includes('submit')) {
      event.preventDefault()
      submit()
      return
    }
    pressOkLogic(questionId)
  }

  /**
   * Gets all ok buttons
   * @returns {NodeListOf<HTMLButtonElement>}
   */
  function getOkButtons() {
    /**
     * A NodeList of buttons with the class `ok-button`.
     * @type {NodeListOf<HTMLButtonElement>}
     */
    const btns = document.querySelectorAll('button.ok-button')
    return btns
  }

  /**
   * Gets all form buttons
   * @returns {NodeListOf<HTMLButtonElement>}
   */
  function getFormButtons() {
    /**
     * A NodeList of buttons with the class `ok-button`.
     * @type {NodeListOf<HTMLButtonElement>}
     */
    const btns = document.querySelectorAll('.form-button')
    return btns
  }

  // -------------------------------------------------
  // -------------------------------------------------
  // -------------------------------------------------
  // -------------------------------------------------
  // START -------------------------------------------------

  if (detectDevice() === 'mobile') {
    document.documentElement.classList.add('mobile')
  }

  // Load initial state
  //if (getSavedMetaData()) {
  const paysMoreThan10k = getSavedResponse('paysMoreThan10k')
  if (paysMoreThan10k) {
    selectFormButton('paysMoreThan10k', paysMoreThan10k)
  }

  const state = getSavedResponse('state')
  if (state && state !== '') {
    const stateElement = document.getElementById('state-select')
    if (stateElement) stateElement.style.display = 'none'
  }
  const email = getSavedResponse('email')
  if (email) {
    loadFormInput('email', email)
  }

  const name = getSavedResponse('name')
  if (name) {
    loadFormInput('name', name)
  }

  const phone = getSavedResponse('phone')
  if (phone) {
    loadFormInput('phone', phone)
  }

  // if all values are set, enable the submit button
  if (paysMoreThan10k && state && email && name && phone) {
    enableButton('submit-button')
  }
  //}

  // Up/Down button logic
  getButton('up').addEventListener('click', () => {
    let lastIndex = getCurrentQuestion() - 1
    lastIndex = lastIndex > 0 ? lastIndex : 0
    const sectionId = SECTIONS[lastIndex]
    scrollToId(sectionId)
    updateScrollArrows()
  })
  getButton('down').addEventListener('click', () => {
    const nextIndex = getCurrentQuestion() + 1
    const sectionId = SECTIONS[nextIndex]
    scrollToId(sectionId)
    updateScrollArrows()
  })

  // Form button logic
  getFormButtons().forEach((element) => {
    element.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && clickFormButton(e)) {
        pressOk(e, ':')
      }
    })
    element.addEventListener('click', clickFormButton)
  })

  // Form input logic
  const emailInput = getInputById('email')
  const nameInput = getInputById('name')
  const phoneInput = getInputById('phone')
  const stateInput = getInputById('state')

  if (emailInput) {
    emailInput.addEventListener('input', processInput)
  }

  if (nameInput) {
    nameInput.addEventListener('input', processInput)
  }

  if (phoneInput) {
    phoneInput.addEventListener('input', processInput)
  }

  if (stateInput) {
    stateInput.addEventListener('change', processInput)
  }

  // if all values are set, enable submit
  if (TD_QUESTION_IDS.every((q) => getSavedResponse(q))) {
    enableButton('submit-button')
  }

  // Ok button logic
  getOkButtons().forEach((element) =>
    element.addEventListener('click', pressOk),
  )
})
